import {useIntl, type IntlShape} from 'react-intl';
import {Phone, PhoneOff, Video} from 'lucide-react';
import {CallAvatar} from '../components/CallAvatar';
import {BubbleActionButton} from '../components/controls';
import {useElapsed} from '../hooks/useElapsed';
import type {LegActions} from '../hooks/useLegActions';
import type {CallLeg} from '../model/types';
import {callSurfaceMessages as m} from '../messages';
import {legNameOf} from '../model/legName';

const incomingLabelOf = (leg: CallLeg, intl: IntlShape): string => {
    if (leg.engine === 'sip' && leg.line?.label) {
        return intl.formatMessage(m.incomingOnLine, {line: leg.line.label});
    }
    return intl.formatMessage(leg.kind === 'video' ? m.incomingVideoCall : m.incomingCall);
};

const CurrentCallRow = ({leg}: {leg: CallLeg}) => {
    const intl = useIntl();
    const timer = useElapsed(leg.connectedAt);
    return (
        <div className="flex items-center gap-2.5 rounded-xl bg-[var(--phq-call-surface)] px-2.5 py-2" data-testid="waiting-current-call">
            <CallAvatar identity={leg.identity} size={32}/>
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{legNameOf(leg, intl)}</p>
                <p className="text-[13px] tabular-nums text-[var(--phq-call-ink-muted)]">
                    {timer ? intl.formatMessage(leg.held ? m.onHold : m.onCall, {timer}) : intl.formatMessage(leg.held ? m.onHoldNoTimer : m.onCallNoTimer)}
                </p>
            </div>
        </div>
    );
};

interface WaitingCardProps {
    readonly incoming: CallLeg;
    readonly current: CallLeg;
    readonly actions: LegActions;
}

export const WaitingCard = ({incoming, current, actions}: WaitingCardProps) => {
    const intl = useIntl();
    const holdAndAccept = () => {
        actions.setHold(current, true);
        actions.answer(incoming);
    };
    const endAndAccept = () => {
        actions.end(current);
        actions.answer(incoming);
    };
    return (
        <div className="phq-call-swap flex flex-col gap-3" data-testid="call-waiting">
            <CurrentCallRow leg={current}/>
            <div className="flex items-center gap-2.5">
                <span className="phq-call-ring rounded-xl"><CallAvatar identity={incoming.identity} size={40}/></span>
                <div className="min-w-0 flex-1">
                    <p className="truncate text-base font-semibold tabular-nums">{legNameOf(incoming, intl)}</p>
                    <p className="truncate text-[13px] text-[var(--phq-call-ink-muted)]">{incomingLabelOf(incoming, intl)}</p>
                </div>
            </div>
            <div className="flex gap-2">
                <BubbleActionButton
                    tone="danger"
                    icon={<PhoneOff size={16} aria-hidden="true"/>}
                    label={intl.formatMessage(m.decline)}
                    testId="call-waiting-decline"
                    onClick={() => actions.decline(incoming)}
                />
                <BubbleActionButton
                    tone="accept"
                    icon={incoming.kind === 'video' ? <Video size={16} aria-hidden="true"/> : <Phone size={16} aria-hidden="true"/>}
                    label={intl.formatMessage(m.holdAndAccept)}
                    testId="call-waiting-hold-accept"
                    onClick={holdAndAccept}
                />
            </div>
            <div className="flex">
                <BubbleActionButton
                    tone="neutral"
                    label={intl.formatMessage(m.endAndAccept)}
                    testId="call-waiting-end-accept"
                    onClick={endAndAccept}
                />
            </div>
        </div>
    );
};

export const IncomingManyCard = ({incoming, actions}: {incoming: readonly CallLeg[]; actions: LegActions}) => {
    const intl = useIntl();
    return (
        <div className="phq-call-swap flex flex-col gap-3" data-testid="call-incoming-many">
            <div className="flex min-h-8 items-center justify-between gap-2">
                <h2 className="text-[15px] font-semibold">{intl.formatMessage(m.manyTitle, {count: incoming.length})}</h2>
                <button
                    type="button"
                    data-testid="call-incoming-decline-all"
                    onClick={() => incoming.forEach(leg => actions.decline(leg))}
                    className="phq-call-press inline-flex h-8 items-center rounded-[10px] bg-[var(--phq-call-surface)] px-2.5 text-[13px] font-semibold"
                >
                    {intl.formatMessage(m.declineAll)}
                </button>
            </div>
            {incoming.map(leg => {
                const name = legNameOf(leg, intl);
                const others = incoming.filter(other => other.id !== leg.id);
                return (
                    <div key={leg.id} className="flex min-h-14 items-center gap-2.5" data-testid="call-incoming-row">
                        <CallAvatar identity={leg.identity} size={40}/>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold tabular-nums">{name}</p>
                            <p className="truncate text-[13px] text-[var(--phq-call-ink-muted)]">{incomingLabelOf(leg, intl)}</p>
                        </div>
                        <button
                            type="button"
                            aria-label={intl.formatMessage(m.declineName, {name})}
                            onClick={() => actions.decline(leg)}
                            className="phq-call-press inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--phq-call-danger)] text-white"
                        >
                            <PhoneOff size={18} aria-hidden="true"/>
                        </button>
                        <button
                            type="button"
                            aria-label={intl.formatMessage(m.acceptName, {name})}
                            onClick={() => {
                                others.forEach(other => other.phase !== 'incoming' && actions.setHold(other, true));
                                actions.answer(leg);
                            }}
                            className="phq-call-press inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--phq-call-accept)] text-white"
                        >
                            <Phone size={18} aria-hidden="true"/>
                        </button>
                    </div>
                );
            })}
        </div>
    );
};
